import { Icon, useObsinaI18n } from "@obsina/design-system";
import { ChevronRight, Info, Search } from "lucide-react";
import { useId } from "react";
import { ENTITY_ICON_COMPONENT, badgeText, toneVars } from "./GraphView";
import { ENTITY_RING_TOKEN } from "./types";
import type {
  MemoryGraphMessages,
  NarrowClusterCard,
  NarrowClusterEntity,
} from "./types";
import "./memory-graph.css";

export interface NarrowClusterListProps {
  readonly cards: readonly NarrowClusterCard[];
  readonly expandedClusterId: string | null;
  readonly onToggleCluster: (clusterId: string) => void;
  /** Focuses a member entity; omitted → member rows are non-interactive. */
  readonly onFocusEntity?: (nodeId: string) => void;
  readonly messages: MemoryGraphMessages;
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
}

/**
 * The honest 320px fallback (P4 narrow frame): the full canvas is replaced by a
 * cluster list, each card expandable to its member entities. No silhouette of
 * unauthorized data is implied; every count comes from the authorized cards.
 */
export function NarrowClusterList({
  cards,
  expandedClusterId,
  onToggleCluster,
  onFocusEntity,
  messages,
  searchValue,
  onSearchChange,
}: NarrowClusterListProps) {
  return (
    <div className="mg-narrow">
      <div className="mg-ninfo">
        <Icon icon={Info} size={17} />
        <span>{messages.narrow.info}</span>
      </div>
      <div className="mg-nsearch">
        <Icon icon={Search} size={17} />
        <input
          aria-label={messages.narrow.searchPlaceholder}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={messages.narrow.searchPlaceholder}
          type="search"
          value={searchValue}
        />
      </div>
      <p className="mg-nsec-title">{messages.narrow.clustersTitle}</p>
      {cards.map((card) => (
        <ClusterCard
          card={card}
          expanded={card.clusterId === expandedClusterId}
          key={card.clusterId}
          messages={messages}
          onFocusEntity={onFocusEntity}
          onToggleCluster={onToggleCluster}
        />
      ))}
    </div>
  );
}

function ClusterCard({
  card,
  expanded,
  messages,
  onToggleCluster,
  onFocusEntity,
}: {
  card: NarrowClusterCard;
  expanded: boolean;
  messages: MemoryGraphMessages;
  onToggleCluster: (clusterId: string) => void;
  onFocusEntity?: (nodeId: string) => void;
}) {
  const listId = useId();
  return (
    <div className={`mg-ccard${expanded ? " mg-ccard--open" : ""}`}>
      <button
        aria-controls={listId}
        aria-expanded={expanded}
        aria-label={
          expanded
            ? messages.narrow.collapse(card.label)
            : messages.narrow.expand(card.label)
        }
        className="mg-ccard__head"
        onClick={() => onToggleCluster(card.clusterId)}
        type="button"
      >
        <span
          aria-hidden="true"
          className="mg-cvig"
          style={toneVars(card.colorToken)}
        >
          {card.vignette.map((dot, index) => (
            <span
              className={`mg-cvig__dot${dot.isHub ? " mg-cvig__dot--hub" : ""}`}
              key={index}
              style={{
                insetInlineStart: `${dot.position.x * 100}%`,
                insetBlockStart: `${dot.position.y * 100}%`,
                inlineSize: `${dot.isHub ? 10 : 6}px`,
                blockSize: `${dot.isHub ? 10 : 6}px`,
              }}
            />
          ))}
        </span>
        <span className="mg-ccard__main">
          <span className="mg-ccard__name">{card.label}</span>
          <span className="mg-ccard__sub">
            {messages.narrow.clusterSub(card.entityCount, card.evidenceCount)}
          </span>
        </span>
        <Icon className="mg-ccard__chev" icon={ChevronRight} size={18} />
      </button>
      {expanded && (
        <ul className="mg-ccard__list" id={listId}>
          {card.entities.map((entity) => (
            <li key={entity.nodeId}>
              <MemberRow
                entity={entity}
                messages={messages}
                onFocusEntity={onFocusEntity}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MemberRow({
  entity,
  messages,
  onFocusEntity,
}: {
  entity: NarrowClusterEntity;
  messages: MemoryGraphMessages;
  onFocusEntity?: (nodeId: string) => void;
}) {
  const { locale } = useObsinaI18n();
  const badge = entity.statusBadge
    ? badgeText(entity.statusBadge, messages, locale)
    : null;

  const inner = (
    <>
      <span
        className="mg-coent__icon"
        style={toneVars(ENTITY_RING_TOKEN[entity.entityType])}
      >
        <Icon icon={ENTITY_ICON_COMPONENT[entity.entityType]} size={14} />
      </span>
      <span className="mg-coent__body">
        <span className="mg-coent__name">{entity.name}</span>
        <span className="mg-coent__sub">
          {messages.narrow.entitySub(
            messages.entityType[entity.entityType],
            entity.linkCount,
          )}
        </span>
      </span>
      {badge && entity.statusBadge && (
        <span
          className="mg-spill mg-coent__spill"
          style={toneVars(entity.statusBadge.tone)}
        >
          <span aria-hidden="true" className="mg-spill__dot" />
          {badge}
        </span>
      )}
    </>
  );

  if (onFocusEntity) {
    return (
      <button
        className="mg-coent"
        onClick={() => onFocusEntity(entity.nodeId)}
        type="button"
      >
        {inner}
      </button>
    );
  }
  return <div className="mg-coent">{inner}</div>;
}
